import { FC, useEffect, useMemo, useState } from 'react';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { MultiSelect } from 'primereact/multiselect';
import { useDebounce } from 'primereact/hooks';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { getClassesForAssignmentDropdown } from '../../graphQLOperations/Classes';
import { ClassesAssignmentDropdownQuery } from '../../graphQLOperations/__generated__/ClassesAssignmentDropdownQuery.graphql';
import { getAssignmentStatusCounts, getSkillFilterOptions } from '../../graphQLOperations/Assignment';
import { AssignmentSkillFilterOptionsQuery } from '../../graphQLOperations/__generated__/AssignmentSkillFilterOptionsQuery.graphql';
import {
  AssignmentStatusCountsQuery,
  AssignmentsFilterInput,
} from '../../graphQLOperations/__generated__/AssignmentStatusCountsQuery.graphql';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';

const SKILL_OPTIONS_LIMIT = 100;
const SKILL_CODE_PATTERN = /^\d+$/;

const useEnterSelectsDropdownOption = () => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const panel = target.closest('.p-multiselect-panel');

      if (event.key === 'Escape' && panel) {
        const close = panel.querySelector('.p-multiselect-close');
        if (!close) return;

        const combobox = document.querySelector('[role="combobox"][aria-expanded="true"]');
        event.preventDefault();
        event.stopPropagation();
        (close as HTMLElement).click();
        (combobox as HTMLElement | null)?.focus();
        return;
      }

      if (event.key !== 'Enter') return;

      const statusOption = target.closest('.nc_status-filter [role="button"]');
      if (statusOption) {
        event.preventDefault();
        event.stopPropagation();
        (statusOption as HTMLElement).click();
        return;
      }

      if (!panel) return;

      const option = target.classList.contains('p-multiselect-filter')
        ? panel.querySelector('.p-multiselect-item.p-focus')
        : target.closest('.p-multiselect-item');
      if (!option) return;

      event.preventDefault();
      event.stopPropagation();
      (option as HTMLElement).click();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);
};

export type AssignmentStatusFilter = 'ALL' | 'ACTIVE';

interface FilterOption {
  id: string;
  label: string;
}

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  status: AssignmentStatusFilter;
  onStatusChange: (value: AssignmentStatusFilter) => void;
  countsFilter: AssignmentsFilterInput;
  countsRefreshKey: number;
  selectedClassIds: string[];
  onSelectedClassIdsChange: (value: string[]) => void;
  hideClassFilter?: boolean;
  selectedSkillIds: string[];
  onSelectedSkillIdsChange: (value: string[]) => void;
}

export const AssignmentsFilters: FC<Props> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  countsFilter,
  countsRefreshKey,
  selectedClassIds,
  onSelectedClassIdsChange,
  hideClassFilter,
  selectedSkillIds,
  onSelectedSkillIdsChange,
}) => {
  const environment = useRelayEnvironment();
  const hiddenClassIds = useHiddenClassIds();
  useEnterSelectsDropdownOption();
  const classesData = useLazyLoadQuery<ClassesAssignmentDropdownQuery>(
    getClassesForAssignmentDropdown,
    {},
    { fetchPolicy: 'store-or-network' },
  );

  const classOptions = useMemo(
    () =>
      (classesData?.classes?.nodes ?? [])
        .filter(classItem => !hiddenClassIds.has(classItem.id))
        .map(classItem => ({ id: classItem.id, label: formatClassDisplayName(classItem) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [classesData, hiddenClassIds],
  );

  const [statusCounts, setStatusCounts] = useState<{ all: number; active: number }>({ all: 0, active: 0 });

  useEffect(() => {
    const subscription = fetchQuery<AssignmentStatusCountsQuery>(environment, getAssignmentStatusCounts, {
      allFilter: countsFilter,
      activeFilter: { ...countsFilter, dueOnFrom: new Date() },
    }).subscribe({
      next: data =>
        setStatusCounts({
          all: data.allAssignments?.totalCount ?? 0,
          active: data.activeAssignments?.totalCount ?? 0,
        }),
    });
    return () => subscription.unsubscribe();
  }, [environment, countsFilter, countsRefreshKey]);

  const statusOptions = useMemo(
    () => [
      { value: 'ALL' as const, label: 'All', count: statusCounts.all },
      { value: 'ACTIVE' as const, label: 'Active only', count: statusCounts.active },
    ],
    [statusCounts],
  );

  const [, debouncedSkillSearch, setSkillSearch] = useDebounce<string>('', 400);
  const [skillOptions, setSkillOptions] = useState<FilterOption[]>([]);
  const [selectedSkillLabels, setSelectedSkillLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    const search = debouncedSkillSearch.trim();
    const searchingByCode = SKILL_CODE_PATTERN.test(search);
    const subscription = fetchQuery<AssignmentSkillFilterOptionsQuery>(environment, getSkillFilterOptions, {
      filter: {
        isActive: true,
        skillName: search && !searchingByCode ? search : null,
        skillCode: searchingByCode ? search : null,
      },
      first: SKILL_OPTIONS_LIMIT,
    }).subscribe({
      next: data =>
        setSkillOptions(
          (data.allSkills?.nodes ?? []).map(skill => ({ id: skill.id, label: `${skill.name} (${skill.code})` })),
        ),
    });
    return () => subscription.unsubscribe();
  }, [environment, debouncedSkillSearch]);

  const skillOptionsWithSelection = useMemo(() => {
    const byId = new Map(skillOptions.map(option => [option.id, option]));
    selectedSkillIds.forEach(id => {
      if (!byId.has(id)) byId.set(id, { id, label: selectedSkillLabels[id] ?? id });
    });
    return [...byId.values()];
  }, [skillOptions, selectedSkillIds, selectedSkillLabels]);

  const handleSkillChange = (ids: string[]) => {
    setSelectedSkillLabels(previous =>
      Object.fromEntries(
        ids.map(id => [id, previous[id] ?? skillOptions.find(option => option.id === id)?.label ?? id]),
      ),
    );
    onSelectedSkillIdsChange(ids);
  };

  return (
    <div className="col-12 flex flex-column gap-3 mb-3">
      <div className="flex flex-column md:flex-row md:align-items-end gap-3 w-full">
        {!hideClassFilter && (
          <div className="flex flex-column gap-1 flex-1 tw-min-w-0">
            <label htmlFor="assignments-class-filter" className="nc_assignments-filter-label">
              Class
            </label>
            <MultiSelect
              inputId="assignments-class-filter"
              value={selectedClassIds}
              options={classOptions}
              optionLabel="label"
              optionValue="id"
              onChange={event => onSelectedClassIdsChange(event.value)}
              placeholder="Select a class"
              selectedItemsLabel="{0} classes"
              maxSelectedLabels={1}
              filter
              disabled={!classOptions.length}
              className="w-full nc_assignments-filter-select"
            />
          </div>
        )}

        <div className="flex flex-column gap-1 flex-1 tw-min-w-0">
          <label htmlFor="assignments-skill-filter" className="nc_assignments-filter-label">
            Skill
          </label>
          <MultiSelect
            inputId="assignments-skill-filter"
            value={selectedSkillIds}
            options={skillOptionsWithSelection}
            optionLabel="label"
            optionValue="id"
            onChange={event => handleSkillChange(event.value)}
            placeholder="Select skill"
            selectedItemsLabel="{0} skills"
            maxSelectedLabels={1}
            filter
            onFilter={event => setSkillSearch(event.filter)}
            resetFilterOnHide={false}
            emptyFilterMessage="No skills found"
            panelFooterTemplate={
              skillOptions.length >= SKILL_OPTIONS_LIMIT ? (
                <p className="nc_assignments-filter-note">
                  {`Showing ${skillOptions.length} of many results. Can't find it? Type to search.`}
                </p>
              ) : null
            }
            className="w-full nc_assignments-filter-select"
          />
        </div>

        <div className="flex flex-column gap-1">
          <span id="assignments-status-label" className="nc_assignments-filter-label">
            Status
          </span>
          <SelectButton
            value={status}
            options={statusOptions}
            optionValue="value"
            itemTemplate={(option: (typeof statusOptions)[number]) => (
              <>
                {option.label}
                <span className="nc_status-filter__count">({option.count})</span>
              </>
            )}
            onChange={event => event.value && onStatusChange(event.value as AssignmentStatusFilter)}
            className="nc_status-filter"
            pt={{ button: { tabIndex: 0 } }}
            aria-labelledby="assignments-status-label"
          />
        </div>
      </div>

      <label htmlFor="assignments-search" className="tw-sr-only">
        Search by assignment name
      </label>
      <IconField iconPosition="left" className="nc_assignments-search">
        <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
        <InputText
          id="assignments-search"
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          placeholder="Search by assignment name"
          className="w-full"
        />
      </IconField>
    </div>
  );
};
